import React, { useState } from "react";
import { Minus, Plus, Check, CalendarDays, MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { euro, formatDateLong } from "@/lib/format";
import client from "@/lib/api";
import { toast } from "sonner";
import { TRANSPORT_OPTIONS } from "@/lib/eventConstants";

const DEFAULT_TICKET_FIELDS = ["Prénom", "Nom", "Email", "Ville / Code postal", "Mode de transport", "Besoins accessibilité"];
const TRANSPORT_OPTS = TRANSPORT_OPTIONS;
const DIET_OPTS = ["Aucun", "Végétarien", "Végan", "Sans gluten", "Halal", "Casher"];
const SELECT_FIELDS = { "Mode de transport": TRANSPORT_OPTS, "Régime alimentaire": DIET_OPTS };
const STEPS = ["Billets", "Coordonnées", "Paiement", "Confirmation"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Shared 4-step registration journey. Demo mode (Branding) = navigation only.
// interactive mode (public landing) = real submit to /public/register/{slug} + Stripe.
export default function RegistrationPreview({ cfg, event, guests = [], interactive = false, slug }) {
  const [step, setStep] = useState(1);
  const [globalCode, setGlobalCode] = useState("");
  const [usedCode, setUsedCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [applied, setApplied] = useState({});
  const [qtys, setQtys] = useState({});
  const [prefill, setPrefill] = useState(null);
  const [vals, setVals] = useState({});
  const [cgu, setCgu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null); // {code, discountPct, ticketIds}
  const [promoError, setPromoError] = useState("");
  const color = cfg?.primaryColor || event?.color || "#4f46e5";
  const tickets = cfg?.tickets || [];

  const applyPromo = () => {
    const c = promoCode.trim().toUpperCase();
    setPromoError("");
    if (!c) return;
    const cfgCode = (cfg?.promoCode || "").toUpperCase();
    const cfgDisc = Number(cfg?.promoDiscountPct || 0);
    if (cfgCode && cfgCode === c && cfgDisc > 0) {
      const matches = tickets.filter((t) => Number(t.price) > 0).map((t) => ({ ticketId: t.id, discountPct: cfgDisc }));
      setPromoApplied({ code: c, matches });
      toast.success(`✅ Code "${c}" appliqué (-${cfgDisc}%)`);
      return;
    }
    // Legacy: per-ticket promoCodes[]
    const matches = [];
    tickets.forEach((t) => {
      (t.promoCodes || []).forEach((p) => {
        if ((p.code || "").toUpperCase() === c && Number(p.discountPct) > 0) matches.push({ ticketId: t.id, discountPct: Number(p.discountPct) });
      });
    });
    if (!matches.length) { setPromoError("Code promo non reconnu"); return; }
    setPromoApplied({ code: c, matches });
    toast.success(`✅ Code "${c}" appliqué (-${matches[0].discountPct}%)`);
  };

  const getV = (k, f) => vals[`${k}::${f}`] ?? "";
  const setV = (k, f, v) => setVals((p) => ({ ...p, [`${k}::${f}`]: v }));

  const setQty = (t, delta) => setQtys((p) => {
    const cur = p[t.id] || (applied[t.id] ? 1 : 0);
    let cap;
    if (prefill?.kind === "structure") {
      cap = 1; // structure : 1 personne = 1 billet, pas d'accompagnants
    } else if (prefill) {
      const maxTotal = (prefill.maxCompanions || 0) + 1;
      const others = Object.entries(p).reduce((s, [id, q]) => id === t.id ? s : s + (q || 0), 0);
      cap = Math.max(0, maxTotal - others);
    } else if (t.accessCode && applied[t.id]) {
      cap = 1; // Code d'accès de billet caché : 1 billet par saisie
    } else if (t.accessCode) {
      cap = t.maxPerCode ?? 999;
    } else {
      cap = 20;
    }
    return { ...p, [t.id]: Math.max(0, Math.min(cap, cur + delta)) };
  });

  const tryGlobalCode = async () => {
    const code = globalCode.trim().toUpperCase();
    if (!code) { setCodeError("Code non reconnu — vérifiez le code reçu par email"); return; }
    // Interactive mode (public landing) → server-side validation
    if (interactive && slug) {
      try {
        const { data } = await client.post(`/public/landing/${slug}/validate-code`, { code });
        if (data.kind === "guest" || data.kind === "structure") {
          if (data.ticketId) { setApplied((p) => ({ ...p, [data.ticketId]: true })); setQtys((p) => ({ ...p, [data.ticketId]: 1 })); }
          setPrefill({
            firstName: data.guest.firstName, lastName: data.guest.lastName, email: data.guest.email,
            category: data.guest.category,
            structureName: data.guest.structureName || "",
            kind: data.kind,
            structureRemaining: data.structureRemaining,
            maxCompanions: data.kind === "structure" ? 0 : (data.guest.maxCompanions || 0),
          });
        } else if (data.kind === "ticket" && data.ticketId) {
          setApplied((p) => ({ ...p, [data.ticketId]: true }));
          setQtys((p) => ({ ...p, [data.ticketId]: Math.max(1, p[data.ticketId] || 0) }));
        }
        setUsedCode(code); setGlobalCode(""); setCodeError("");
      } catch (e) {
        setCodeError(e?.response?.data?.detail || "Code non reconnu — vérifiez le code reçu par email");
      }
      return;
    }
    // Demo mode (Branding preview) → local matching against provided guests
    const matchGuest = (guests || []).find((g) => g.specialCode?.toUpperCase() === code);
    if (matchGuest) {
      const mt = tickets.find((t) => t.linkedCategory === matchGuest.category);
      if (mt) { setApplied((p) => ({ ...p, [mt.id]: true })); setQtys((p) => ({ ...p, [mt.id]: 1 })); }
      setPrefill({ firstName: matchGuest.firstName, lastName: matchGuest.lastName, email: matchGuest.email, category: matchGuest.category, maxCompanions: matchGuest.maxCompanions || 0 });
      setUsedCode(code); setGlobalCode(""); setCodeError(""); return;
    }
    const mt = tickets.find((t) => t.accessCode?.toUpperCase() === code);
    if (mt) {
      const maxAllowed = mt.maxPerCode ?? 999;
      if ((qtys[mt.id] || 0) >= maxAllowed) { setCodeError("Quota atteint"); return; }
      setApplied((p) => ({ ...p, [mt.id]: true })); setQtys((p) => ({ ...p, [mt.id]: Math.max(1, p[mt.id] || 0) }));
      setUsedCode(code); setGlobalCode(""); setCodeError(""); return;
    }
    setCodeError("Code non reconnu — vérifiez le code reçu par email");
  };

  const visibleTickets = tickets.filter((t) => !t.hidden || applied[t.id] || (prefill && (!t.linkedCategory || t.linkedCategory === prefill.category)));
  const priceOf = (t) => {
    const base = Number(t.price || 0);
    if (!promoApplied) return base;
    const m = promoApplied.matches.find((x) => x.ticketId === t.id);
    return m ? +(base * (1 - m.discountPct / 100)).toFixed(2) : base;
  };
  const total = tickets.reduce((a, t) => a + (qtys[t.id] || 0) * priceOf(t), 0);
  const totalBase = tickets.reduce((a, t) => a + (qtys[t.id] || 0) * Number(t.price || 0), 0);
  const totalQty = tickets.reduce((a, t) => a + (qtys[t.id] || 0), 0);
  const selected = tickets.filter((t) => (qtys[t.id] || 0) > 0);

  const collectAndValidate = () => {
    const items = selected.map((t) => ({ ticketId: t.id, quantity: qtys[t.id] }));
    const attendees = []; let holder = null;
    const missing = [];
    const isOptional = (t, f) => (t.optionalFields || []).includes(f) || f === "Besoins accessibilité" || f === "Code promo";
    const customByLabel = Object.fromEntries((cfg.customFields || []).map((field) => [field.label, field]));
    selected.forEach((t) => {
      for (let i = 0; i < qtys[t.id]; i++) {
        const k = `${t.id}-${i}`;
        const fields = (i > 0 ? t.companionFields : t.fields) || DEFAULT_TICKET_FIELDS;
        const o = { role: i > 0 ? "companion" : "holder", ticket: t.name, ticketId: t.id };
        fields.forEach((f) => {
          const v = i === 0 && f === "Email" && prefill?.email && prefill.kind !== "structure" ? prefill.email : getV(k, f);
          o[f] = v;
          if (!isOptional(t, f) && !String(v || "").trim()) {
            missing.push(`${i === 0 ? "Titulaire" : `Accompagnant ${i}`} · ${t.name} · ${f}`);
          }
        });
        if (o["Email"] && !EMAIL_RE.test(String(o["Email"]).trim())) {
          missing.push(`${i === 0 ? "Titulaire" : `Accompagnant ${i}`} · ${t.name} · Email invalide`);
        }
        fields.forEach((f) => {
          const field = customByLabel[f];
          const value = String(o[f] || "").trim();
          if (!field || !value) return;
          if (field.type === "email" && !EMAIL_RE.test(value)) missing.push(`${t.name} · ${f} : email invalide`);
          if (field.type === "number" && (!(Number(value) > 0))) missing.push(`${t.name} · ${f} : nombre positif requis`);
          if (field.type === "date" && !/^\d{4}-\d{2}-\d{2}$/.test(value)) missing.push(`${t.name} · ${f} : date invalide`);
        });
        o.annuaire = !!vals[`${k}::annuaire`]; o.contact = !!vals[`${k}::contact`];
        attendees.push(o); if (!holder) holder = o;
      }
    });
    return { items, attendees, holder, missing };
  };

  const goStep3 = () => {
    if (!interactive) { setStep(3); return; }
    if (!cgu) { toast.error("Vous devez accepter les CGU"); return; }
    const { missing } = collectAndValidate();
    if (missing.length) {
      toast.error(`Veuillez renseigner tous les champs (${missing.length} manquant${missing.length > 1 ? "s" : ""}) — chaque participant reçoit son propre QR code nominatif unique.`);
      return;
    }
    setStep(3);
  };

  const doSubmit = async () => {
    if (!interactive) { setStep(4); return; }
    const { items, attendees, holder, missing } = collectAndValidate();
    if (!cgu) { toast.error("Vous devez accepter les CGU"); setStep(2); return; }
    if (missing.length) {
      toast.error(`Veuillez renseigner tous les champs (${missing.length} manquant${missing.length > 1 ? "s" : ""}) — chaque participant reçoit son propre QR code nominatif unique.`);
      setStep(2); return;
    }
    setLoading(true);
    try {
      const { data } = await client.post(`/public/register/${slug}`, {
        firstName: holder["Prénom"] || "", lastName: holder["Nom"] || "", email: holder["Email"] || "",
        items, attendees, accessCode: usedCode,
        promoCode: promoApplied?.code || "",
        customFields: { annuaire: holder.annuaire, contact: holder.contact, cgu, promoCode: promoApplied?.code || "" },
        originUrl: window.location.origin,
      });
      if (data.free) setStep(4);
      else if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Une erreur est survenue."); }
    finally { setLoading(false); }
  };

  const renderStep = (n) => (
    <button key={n} onClick={() => setStep(n)} data-testid={`reg-step-${n}`}
      className={`text-xs font-semibold px-2.5 py-1 rounded-md ${step === n ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600"}`}>{n}. {STEPS[n - 1]}</button>
  );

  const renderHolder = (ticket, companion, idx) => {
    const k = `${ticket.id}-${idx}`;
    const fields = (companion ? ticket.companionFields : ticket.fields) || DEFAULT_TICKET_FIELDS;
    const has = (f) => fields.includes(f);
    const wide = ["Email", "Ville / Code postal", "Besoins accessibilité", "Poste", "ID interne", "Entreprise", "Code promo"];
    const customByLabel = Object.fromEntries((cfg.customFields || []).map((cf) => [cf.label, cf]));
    const isHolder = !companion && idx === 0;
    const inputFor = (f) => {
      const cf = customByLabel[f];
      if (cf) {
        if (cf.type === "select" || cf.type === "radio") return <Select key={f} value={getV(k, f)} onValueChange={(v) => setV(k, f, v)}><SelectTrigger className="h-8 text-sm col-span-2"><SelectValue placeholder={f} /></SelectTrigger><SelectContent>{(cf.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>;
        if (cf.type === "checkbox") return <label key={f} className="flex items-center gap-1.5 text-[11px] col-span-2"><Checkbox className="h-3 w-3" checked={!!vals[`${k}::${f}`]} onCheckedChange={(v) => setV(k, f, v)} />{f}</label>;
        const type = ["number", "date", "email"].includes(cf.type) ? cf.type : "text";
        return <Input key={f} type={type} min={type === "number" ? "1" : undefined} placeholder={f} value={getV(k, f)} onChange={(e) => setV(k, f, e.target.value)} className="h-8 text-sm col-span-2" data-testid={`reg-custom-${k}-${f}`} />;
      }
      if (f === "Email" && isHolder && prefill?.email && prefill.kind !== "structure") {
        return <Input key={f} value={prefill.email} readOnly disabled className="h-8 text-sm col-span-2 bg-slate-50 cursor-not-allowed" data-testid={`reg-email-locked-${k}`} title="Email pré-rempli depuis votre invitation" />;
      }
      if (f === "Ville / Code postal") {
        return (
          <div key={f} className="col-span-2">
            <AddressAutocomplete
              value={getV(k, f)}
              onChange={(v) => setV(k, f, v)}
              onResolved={(r) => { setV(k, f, r.formatted); setV(k, "__city", r.city); setV(k, "__postal", r.postal); }}
              placeholder="Ville / Code postal"
              testid={`reg-city-${k}`}
              className=""
            />
          </div>
        );
      }
      if (SELECT_FIELDS[f]) return <Select key={f} value={getV(k, f)} onValueChange={(v) => setV(k, f, v)}><SelectTrigger className="h-8 text-sm" data-testid={`reg-${f}`}><SelectValue placeholder={f} /></SelectTrigger><SelectContent>{SELECT_FIELDS[f].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>;
      if (f === "Nombre de nuitées d'hôtel") return <Input key={f} type="number" min="0" placeholder={f} value={getV(k, f)} onChange={(e) => setV(k, f, e.target.value)} className="h-8 text-sm" data-testid={`reg-nights-${k}`} />;
      if (f === "Prénom" && isHolder && prefill?.firstName && prefill.kind !== "structure" && !getV(k, f)) { setTimeout(() => setV(k, f, prefill.firstName), 0); }
      if (f === "Nom" && isHolder && prefill?.lastName && prefill.kind !== "structure" && !getV(k, f)) { setTimeout(() => setV(k, f, prefill.lastName), 0); }
      return <Input key={f} placeholder={f} value={getV(k, f)} onChange={(e) => setV(k, f, e.target.value)} className={`h-8 text-sm ${wide.includes(f) ? "col-span-2" : ""}`} />;
    };
    return (
      <div className={`rounded-lg p-3 mb-2 ${companion ? "" : "border border-slate-200"}`} style={companion ? { background: "#fff7ed", border: "1px solid #ffedd5" } : {}}>
        <p className="text-xs font-semibold mb-2 flex items-center gap-2">{companion ? "Accompagnant" : `Titulaire — ${ticket.name}`}</p>
        <div className="grid grid-cols-2 gap-2">
          {fields.map((f) => inputFor(f))}
          {!has("Email") && <label className="flex items-center gap-1.5 text-[11px] col-span-2"><Checkbox className="h-3 w-3" />Sans email (enfant, etc.)</label>}
          <div className="col-span-2 mt-1 text-[11px] space-y-1">
            <p className="font-semibold">Confidentialité *</p>
            <label className="flex items-center gap-1.5"><Checkbox className="h-3 w-3" checked={!!vals[`${k}::annuaire`]} onCheckedChange={(v) => setV(k, "annuaire", v)} />Souhaite apparaître dans l'annuaire des participants *</label>
            <label className="flex items-center gap-1.5"><Checkbox className="h-3 w-3" checked={!!vals[`${k}::contact`]} onCheckedChange={(v) => setV(k, "contact", v)} />Accepte d'être contacté(e) par d'autres participants *</label>
            <label className="flex items-center gap-1.5"><Checkbox className="h-3 w-3" checked={!!vals[`${k}::photos`]} onCheckedChange={(v) => setV(k, "photos", v)} data-testid={`reg-photos-${k}`} />Accepte d'apparaître dans les photos et vidéos prises lors de l'événement</label>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-display font-semibold">Aperçu du parcours d'inscription</h3>
        <div className="flex gap-1.5 flex-wrap">{STEPS.map((_, i) => renderStep(i + 1))}</div>
      </div>
      <div className="bg-slate-100 rounded-xl p-4 max-h-[75vh] overflow-y-auto">
        <div className="bg-white rounded-xl overflow-hidden shadow">
          <div className="h-24" style={{ background: cfg.bannerImage ? undefined : color }}>{cfg.bannerImage && <img src={cfg.bannerImage} alt="" className="h-full w-full object-cover" />}</div>
          <div className="p-5">
            <h4 className="font-display font-bold text-lg">{event.name}</h4>

            {step === 1 && (
              <>
                <p className="text-xs text-slate-500 mb-3">Étape 1 · Billets</p>
                <div className="space-y-2">
                  {visibleTickets.map((t) => {
                    const isLocked = (prefill?.kind === "structure" && applied[t.id]) || (!!t.accessCode && applied[t.id] && !prefill);
                    return (
                    <div key={t.id} className="border rounded-lg p-3 flex justify-between items-center" style={{ borderColor: applied[t.id] ? color : "#e2e8f0" }}>
                      <div><p className="font-semibold text-sm">{t.name}</p><p className="text-xs text-slate-500">{t.description}</p><p className="text-xs font-bold mt-0.5" style={{ color }}>{t.price ? euro(t.price) : "Gratuit"}</p>{isLocked && <p className="text-[10px] text-slate-400 mt-0.5">🔒 Quantité fixée à 1 pour ce code</p>}</div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setQty(t, -1)} disabled={isLocked} className="h-7 w-7 rounded-md border flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`qty-minus-${t.id}`}><Minus className="h-3.5 w-3.5" /></button>
                        <span className="w-6 text-center text-sm font-semibold" data-testid={`qty-${t.id}`}>{qtys[t.id] || 0}</span>
                        <button onClick={() => setQty(t, 1)} disabled={isLocked} className="h-7 w-7 rounded-md border flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`qty-plus-${t.id}`}><Plus className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                    );
                  })}
                </div>
                {tickets.some((t) => t.hidden) && (
                  <div className="mt-4">
                    <p className="text-xs text-slate-500 mb-2 text-center" data-testid="or-access-code">— Ou saisissez votre code d'accès —</p>
                    <div className="flex gap-2">
                      <Input placeholder="Code d'accès" value={globalCode} onChange={(e) => setGlobalCode(e.target.value)} data-testid="global-code" className="font-mono text-sm" />
                      <Button size="sm" onClick={tryGlobalCode} data-testid="validate-code" style={{ background: color }}>Valider</Button>
                    </div>
                    {codeError && !prefill && <p className="text-xs text-rose-600 mt-1" data-testid="code-error">{codeError}</p>}
                    {prefill && (
                      <div className="mt-2 p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-800 flex items-center justify-between gap-2" data-testid="code-recognized">
                        {prefill.kind === "structure"
                          ? <span>🏢 Code structure reconnu — <b>{prefill.structureName || `${prefill.firstName} ${prefill.lastName}`}</b>. Saisissez <b>vos propres coordonnées</b> (nom, prénom, email) — le billet est nominatif pour vous.{prefill.structureRemaining != null && <> ({prefill.structureRemaining} place(s) restante(s) sur ce code)</>}</span>
                          : <span>✅ Invité reconnu — <b>{prefill.firstName} {prefill.lastName}</b> · {prefill.email} · vous pouvez acheter jusqu'à <b>{(prefill.maxCompanions || 0) + 1}</b> billet{((prefill.maxCompanions || 0) + 1) > 1 ? "s" : ""} ({prefill.maxCompanions} accompagnant{prefill.maxCompanions > 1 ? "s" : ""} max)</span>}
                        <button onClick={() => { setPrefill(null); setApplied({}); setQtys({}); setUsedCode(""); setCodeError(""); }} className="underline shrink-0">Ce n'est pas moi</button>
                      </div>
                    )}
                  </div>
                )}
                <button disabled={!totalQty} onClick={() => setStep(2)} className="w-full mt-4 py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-50" style={{ background: color }} data-testid="reg-next-1">Continuer ({totalQty} billet{totalQty > 1 ? "s" : ""})</button>
              </>
            )}

            {step === 2 && (
              <>
                <p className="text-xs text-slate-500 mb-1">Étape 2 · Coordonnées</p>
                <p className="text-[11px] text-slate-500 bg-amber-50 border border-amber-200 rounded-md p-2 mb-3" data-testid="reg-nominative-notice">ℹ️ Merci de renseigner tous les champs pour <b>chaque participant</b> (titulaire et accompagnants). Chaque personne recevra son propre <b>QR code nominatif unique</b> à présenter à l'entrée de l'événement.</p>
                {selected.map((t) => (
                  <div key={t.id}>{Array.from({ length: qtys[t.id] }).map((_, i) => <React.Fragment key={i}>{renderHolder(t, i > 0, i)}</React.Fragment>)}</div>
                ))}
                <label className="flex items-center gap-1.5 text-[11px] mt-2"><Checkbox className="h-3 w-3" checked={cgu} onCheckedChange={setCgu} data-testid="reg-cgu" />J'accepte les CGU *</label>
                <button onClick={() => (total ? goStep3() : doSubmit())} disabled={loading} className="w-full mt-3 py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-60" style={{ background: color }} data-testid="reg-next-2">{loading ? "…" : total ? "Voir le récapitulatif" : "Finaliser l'inscription"}</button>
              </>
            )}

            {step === 3 && (
              <>
                <p className="text-xs text-slate-500 mb-3">Étape 3 · Paiement</p>
                <div className="space-y-2 text-sm">
                  {selected.map((t) => {
                    const disc = promoApplied?.matches.find((x) => x.ticketId === t.id);
                    return (
                      <div key={t.id} className="flex justify-between">
                        <span>{qtys[t.id]} × {t.name}{disc && <span className="ml-1 text-xs text-emerald-700 font-semibold">(-{disc.discountPct}%)</span>}</span>
                        <b>{euro((qtys[t.id] || 0) * priceOf(t))}{disc && <span className="ml-1 text-xs text-slate-400 line-through font-normal">{euro((qtys[t.id] || 0) * Number(t.price || 0))}</span>}</b>
                      </div>
                    );
                  })}
                  <div className="flex justify-between border-t pt-2 mt-2 text-base"><span className="font-semibold">Total</span><b style={{ color }}>{euro(total)}</b></div>
                  {totalBase > 0 && (
                    <div className="mt-3 p-2 rounded-lg bg-slate-50 border border-slate-200" data-testid="reg-promo-block">
                      <Label className="text-[11px] mb-1 block flex items-center gap-1">🏷️ Code promo</Label>
                      {promoApplied ? (
                        <div className="flex items-center justify-between text-xs bg-emerald-50 border border-emerald-200 rounded p-1.5">
                          <span>✅ <b className="font-mono">{promoApplied.code}</b> appliqué</span>
                          <button className="text-rose-600 underline" onClick={() => { setPromoApplied(null); setPromoCode(""); }} data-testid="reg-promo-remove">Retirer</button>
                        </div>
                      ) : (
                        <div className="flex gap-1.5">
                          <Input placeholder="Entrez un code" value={promoCode} onChange={(e) => setPromoCode(e.target.value)} className="h-8 text-xs font-mono" data-testid="reg-promo-input" />
                          <Button size="sm" onClick={applyPromo} className="h-8" data-testid="reg-promo-apply">Appliquer</Button>
                        </div>
                      )}
                      {promoError && <p className="text-[11px] text-rose-600 mt-1">{promoError}</p>}
                    </div>
                  )}
                </div>
                {total <= 0 && (
                  <div className="mt-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800" data-testid="reg-free-notice">🎟️ Inscription gratuite — aucune coordonnée bancaire requise.</div>
                )}
                <button onClick={doSubmit} disabled={loading} className="w-full mt-4 py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2" style={{ background: color }} data-testid="reg-next-3">{loading ? <><Loader2 className="h-4 w-4 animate-spin" />Enregistrement…</> : "Finaliser mon inscription"}</button>
              </>
            )}

            {step === 4 && (
              <div className="text-center py-4" data-testid="reg-confirmation">
                <div className="h-14 w-14 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-3"><Check className="h-7 w-7 text-indigo-600" /></div>
                <h4 className="font-display font-bold text-lg">Inscription confirmée !</h4>
                <p className="text-sm text-slate-500 mt-1">Votre place pour <b>{(prefill && prefill.kind !== "structure") ? `${prefill.firstName} ${prefill.lastName}` : "vous"}</b> est réservée.<br />Un email de confirmation avec votre billet a été envoyé.</p>
                <div className="text-sm text-slate-600 mt-4 space-y-1">
                  <p className="flex items-center justify-center gap-2"><CalendarDays className="h-4 w-4" />{formatDateLong(event.startDate)}</p>
                  <p className="flex items-center justify-center gap-2"><MapPin className="h-4 w-4" />{event.city || "—"}</p>
                </div>
                <div className="mt-4 p-4 rounded-xl border border-slate-200 text-left">
                  <p className="text-sm font-semibold flex items-center gap-2 mb-1">📲 Vivez l'événement pleinement</p>
                  <p className="text-xs text-slate-500 mb-3">Téléchargez l'application <b>PlanItGreen</b> pour accéder au programme en temps réel, retrouver les autres participants et interagir en direct.</p>
                  <div className="flex gap-2">
                    <span className="flex-1 text-center text-xs text-white rounded-md py-2 bg-slate-900"> App Store</span>
                    <span className="flex-1 text-center text-xs text-white rounded-md py-2 bg-emerald-600">▶ Google Play</span>
                  </div>
                </div>
                {!interactive && <button onClick={() => { setStep(1); setQtys({}); setApplied({}); setPrefill(null); }} className="text-xs text-slate-500 underline mt-4" data-testid="reg-restart">↻ Recommencer la démo</button>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
